import styled, { css, keyframes } from 'styled-components';
import colors from 'styles/colors';

// Painel da petição do modo Atendimento (o "Canvas" do IAGov): folha do
// documento ao lado do chat, com a mesma tipografia do preview do V3.

export const Panel = styled.aside<{ expanded: boolean }>`
  width: min(46vw, 720px);
  min-width: 440px;
  border-left: 1px solid #e5e9e7;
  background: #eef1f4;
  display: flex;
  flex-direction: column;
  min-height: 0;

  ${({ expanded }) =>
    expanded &&
    css`
      width: auto;
      min-width: 0;
      flex: 1;
      border-left: none;
    `}
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px 10px;
  border-bottom: 1px solid #dde2e6;
  background: #f5f7fa;

  h3 {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1f2a37;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const pulso = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

export const Status = styled.span<{ tone: 'neutro' | 'ativo' | 'ok' | 'alerta' }>`
  font-size: 11px;
  font-weight: 600;
  border-radius: 10px;
  padding: 2px 9px;
  white-space: nowrap;
  background: ${({ tone }) =>
    tone === 'ativo'
      ? '#e1ecf7'
      : tone === 'ok'
      ? colors.$success_light_bg
      : tone === 'alerta'
      ? colors.$danger_light_bg
      : '#e5e9e7'};
  color: ${({ tone }) =>
    tone === 'ativo'
      ? '#23405c'
      : tone === 'ok'
      ? colors.$dark_green
      : tone === 'alerta'
      ? colors.$danger
      : '#5c6b7a'};

  ${({ tone }) =>
    tone === 'ativo' &&
    css`
      animation: ${pulso} 1.4s ease-in-out infinite;
    `}
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 12px;
  border-bottom: 1px solid #dde2e6;
  background: #f5f7fa;
  flex-wrap: wrap;
`;

export const ToolButton = styled.button<{ primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid transparent;
  background: none;
  border-radius: 6px;
  padding: 5px 9px;
  font-size: 12.5px;
  color: #23405c;
  cursor: pointer;

  i {
    font-size: 18px;
  }

  &:hover:not(:disabled) {
    background: #e2e7ea;
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }

  ${({ primary }) =>
    primary &&
    css`
      background: #23405c;
      color: ${colors.$white};

      &:hover:not(:disabled) {
        background: #2c5278;
      }
    `}
`;

export const Spacer = styled.span`
  flex: 1;
`;

export const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 20px 28px;
`;

// A "folha": a petição como sai no Word (ABNT do gerador do agente: Arial 12,
// entrelinha 1,5, recuo de 1,25 cm, título 1 centralizado, citação recuada em
// 4 cm com corpo 10). Vale para a leitura e para o editor visual
export const Sheet = styled.div`
  background: ${colors.$white};
  border: 1px solid #dde2e6;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(31, 42, 55, 0.08);
  padding: 36px 40px;
  max-width: 820px;
  margin: 0 auto;
  font-family: Arial, Helvetica, sans-serif;
  color: #111;
  font-size: 12pt;
  line-height: 1.5;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 12pt;
    font-weight: 700;
    line-height: 1.5;
    color: #111;
    text-transform: none;
    letter-spacing: 0;
    margin: 6pt 0 18pt;
  }

  h1 {
    text-align: center;
    margin-top: 12pt;
  }

  > :first-child,
  .ProseMirror > :first-child {
    margin-top: 0;
  }

  p {
    text-align: justify;
    text-indent: 1.25cm;
    margin: 0 0 18pt;
  }

  li > p,
  td p,
  th p,
  blockquote p {
    text-indent: 0;
  }

  li > p {
    margin: 0;
  }

  blockquote {
    margin: 0 0 18pt 4cm;
    padding: 0;
    border: none;
    font-size: 10pt;
    line-height: 1.15;
    font-style: normal;

    p {
      margin: 0 0 6pt;
    }
  }

  ul,
  ol {
    padding-left: 1.25cm;
    margin: 0 0 18pt;
  }

  u {
    text-decoration: underline;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 0 0 18pt;
    font-size: 11pt;
    line-height: 1.3;

    th,
    td {
      border: 1px solid #8a948f;
      padding: 4px 8px;
      vertical-align: top;
    }

    th {
      background: #f5f7fa;
    }

    p {
      margin: 0;
      text-align: left;
    }
  }

  .tableWrapper {
    overflow-x: auto;
  }

  hr {
    border: none;
    border-top: 1px solid #c3ccd4;
    margin: 0 0 18pt;
  }

  code {
    font-size: 10.5pt;
    color: inherit;
    background: #f5f7fa;
    padding: 0 3px;
    border-radius: 3px;
  }

  /* Linha só com <br>: espaço em branco, da altura de uma linha */
  .bloco-espaco {
    height: 1.5em;
    margin: 0;
  }

  pre,
  .bloco-preservado {
    white-space: pre-wrap;
    font-family: SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 10.5pt;
    line-height: 1.4;
    background: #f5f7fa;
    color: #5c6b7a;
    border-left: 3px solid #c3ccd4;
    padding: 8px 10px;
    margin: 0 0 18pt;
  }

  /* Lacunas e notas de uso: só destaque, o texto continua texto */
  .lacuna {
    background: #fff1b8;
    border-bottom: 1px dashed #c99400;
    border-radius: 2px;
    padding: 0 1px;
    color: inherit;
  }

  .nota-de-uso {
    background: #fde2e1;
    color: #b42318;
    border-radius: 2px;
    padding: 0 1px;
  }

  .lacuna-foco {
    outline: 2px solid #d97706;
    outline-offset: 1px;
  }

  /* Editor visual */
  .ProseMirror {
    outline: none;
    min-height: 240px;
  }

  .ProseMirror-selectednode {
    outline: 2px solid #8fb3d9;
  }

  .selectedCell {
    background: #e1ecf7;
  }

  @media print {
    .lacuna,
    .nota-de-uso {
      background: none;
      border: none;
    }
  }
`;

export const Editor = styled.textarea`
  display: block;
  width: 100%;
  max-width: 820px;
  min-height: 100%;
  margin: 0 auto;
  border: 1px solid #c3ccd4;
  border-radius: 4px;
  padding: 18px 20px;
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
  line-height: 1.6;
  color: #1f2a37;
  resize: none;
  background: ${colors.$white};

  &:focus {
    outline: none;
    border-color: #2c5278;
    box-shadow: 0 0 0 2px rgba(44, 82, 120, 0.15);
  }
`;

export const Banner = styled.div<{ tone: 'alerta' | 'info' }>`
  margin: 10px 16px 0;
  border-radius: 6px;
  padding: 9px 12px;
  font-size: 12.5px;
  background: ${({ tone }) => (tone === 'alerta' ? colors.$danger_light_bg : '#e1ecf7')};
  color: ${({ tone }) => (tone === 'alerta' ? colors.$danger : '#23405c')};

  button {
    border: none;
    background: none;
    font-weight: 700;
    color: inherit;
    text-decoration: underline;
    cursor: pointer;
    margin-left: 8px;
    padding: 0;
  }

  a {
    font-weight: 700;
    margin-left: 6px;
  }
`;

export const Vazio = styled.div`
  text-align: center;
  color: #8a948f;
  font-size: 13px;
  padding: 48px 16px;

  i {
    display: block;
    font-size: 36px;
    margin-bottom: 8px;
  }
`;

// Card do documento na bolha do assistente (o corpo fica só no painel)
export const DocumentCard = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 460px;
  text-align: left;
  border: 1px solid #dde2e6;
  border-radius: 8px;
  background: ${colors.$white};
  padding: 10px 14px;
  margin: 8px 0 4px;
  cursor: pointer;

  i {
    font-size: 26px;
    color: #23405c;
  }

  strong {
    display: block;
    font-size: 13.5px;
    color: #1f2a37;
  }

  span {
    font-size: 12px;
    color: #5c6b7a;
  }

  &:hover {
    border-color: #2c5278;
    box-shadow: inset 0 0 0 1px #2c5278;
  }
`;

export const Select = styled.select`
  display: block;
  width: 100%;
  margin-top: 4px;
  margin-bottom: 12px;
  padding: 6px 8px;
  border: 1px solid #c3ccd4;
  border-radius: 4px;
  font-size: 13px;
`;

// Chip do cabeçalho: lacunas em aberto
export const Chip = styled.button<{ tone: 'lacuna' | 'nota' }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border: none;
  border-radius: 10px;
  padding: 2px 6px 2px 9px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  background: ${({ tone }) => (tone === 'lacuna' ? '#fff1b8' : '#fde2e1')};
  color: ${({ tone }) => (tone === 'lacuna' ? '#7a5b00' : '#b42318')};

  i {
    font-size: 15px;
  }
`;

// Menu "Mais" da barra do painel
export const MenuWrapper = styled.div`
  position: relative;
`;

export const Menu = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  z-index: 20;
  min-width: 230px;
  background: ${colors.$white};
  border: 1px solid #dde2e6;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(31, 42, 55, 0.14);
  padding: 4px;

  button {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    border: none;
    background: none;
    border-radius: 6px;
    padding: 7px 10px;
    font-size: 12.5px;
    color: #1f2a37;
    text-align: left;
    cursor: pointer;

    i {
      font-size: 18px;
      color: #5c6b7a;
    }

    &:hover:not(:disabled) {
      background: #eef1f4;
    }

    &:disabled {
      opacity: 0.45;
      cursor: default;
    }
  }
`;

// Volta a acompanhar a escrita depois que o usuário rolou para cima
export const Acompanhar = styled.button`
  position: sticky;
  bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 10px auto 0;
  border: none;
  border-radius: 16px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  background: #23405c;
  color: ${colors.$white};
  box-shadow: 0 3px 10px rgba(31, 42, 55, 0.25);
  cursor: pointer;

  i {
    font-size: 17px;
  }
`;
